import React, { useCallback, useEffect, useReducer } from 'react'
import classnames from 'classnames'

import { analytics } from 'helpers'
import { AUTH_STEPS as STEPS } from 'constants/auth'

import Overlay from './components/Overlay'
import ChooseAuth from './steps/ChooseAuthStep'
import PhoneAuth from './steps/PhoneAuthStep'
import CodeVerification from './steps/CodeValidationStep'
import Signup from './steps/SignupFormStep'
import Modal from './components/Modal'

import { OVERLAY } from './styles'
import ProfileStep from 'components/AuthModal/steps/ProfileStep'
import UserDataStep from 'components/AuthModal/steps/UserDataStep'

const { CHOOSE_AUTH, MY_NUMBER, CODE_VERIFICATION, SIGN_UP, PROFILE, USER_DATA } = STEPS

const ACTIVATE_AUTOFOCUS = 'ACTIVATE_AUTOFOCUS'
const MOVE_TO_PHONE_AUTH = 'MOVE_TO_PHONE_AUTH'
const MOVE_TO_CODE_VERIFICATION = 'MOVE_TO_CODE_VERIFICATION'
const MOVE_TO_SIGNUP = 'MOVE_TO_SIGNUP'

const initialState = {
  step: STEPS.MY_NUMBER,
  autoFocus: true,
  user: {},
  verificationCode: null,
  phoneNumberIsVerified: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIVATE_AUTOFOCUS:
      return { ...state, autoFocus: true }
    case MOVE_TO_PHONE_AUTH:
      // on every step change set autofocus to false or animation breaks
      return { ...state, step: MY_NUMBER, autoFocus: false }
    case MOVE_TO_CODE_VERIFICATION:
      return { step: CODE_VERIFICATION, autoFocus: true, user: action.payload }
    case MOVE_TO_SIGNUP:
      return { step: SIGN_UP, autoFocus: true, phoneNumberIsVerified: true, user: action.payload }
    default:
      throw new Error()
  }
}

const ShowModalStep = ({
  step,
  autoFocus,
  onClose,
  dispatch,
  user,
  onComplete,
  signupStep,
  data,
}) => {
  const handleComplete = useCallback(
    signup => {
      onComplete && onComplete(signup)
      window.scrollTo(0, 0)
    },
    [onComplete]
  )

  switch (step) {
    case CHOOSE_AUTH:
      return (
        <ChooseAuth
          autoFocus={autoFocus}
          onSubmit={() => dispatch({ type: MOVE_TO_PHONE_AUTH })}
          onClose={onClose}
        />
      )
    case MY_NUMBER:
      return (
        <PhoneAuth
          autoFocus={autoFocus}
          onSubmit={phoneNumber => {
            dispatch({ type: MOVE_TO_CODE_VERIFICATION, payload: { phoneNumber } })
          }}
          onClose={
            onClose
              ? () => {
                  analytics.segmentTrack('Anonymous Closed Sign In/Sign Up Modal')
                  onClose()
                }
              : null
          }
          user={user}
        />
      )
    case CODE_VERIFICATION:
      return (
        <CodeVerification
          autoFocus={autoFocus}
          onSubmit={data => {
            !data?.completedSignup
              ? dispatch({
                  type: MOVE_TO_SIGNUP,
                  payload: { jwt: data.jwt, phoneNumber: data.phoneNumber },
                })
              : handleComplete()
          }}
          user={user}
          onClose={() => {
            analytics.segmentTrack('Anonymous Went Back To Enter Phone Number Step')
            dispatch({ type: MOVE_TO_PHONE_AUTH })
          }}
        />
      )
    case SIGN_UP:
      return (
        <Signup
          autoFocus={autoFocus}
          onSubmit={onComplete}
          user={user}
          signupStep={signupStep}
          onClose={() => {
            analytics.segmentTrack('Anonymous Went Back To Enter Phone Number Step')
            dispatch({ type: MOVE_TO_PHONE_AUTH })
          }}
        />
      )
    case PROFILE:
      return (
        <ProfileStep autoFocus={autoFocus} onSubmit={onComplete} user={user} onClose={onClose} />
      )
    case USER_DATA:
      return (
        <UserDataStep
          autoFocus={autoFocus}
          onSubmit={onComplete}
          user={user}
          onClose={onClose}
          data={data}
        />
      )
    default:
      return null
  }
}

const InternalModal = ({
  onClose,
  onComplete,
  event,
  authStep,
  signupStep,
  data,
  showCloseButton,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    ...(authStep ? { step: authStep } : null),
  })
  const { autoFocus, step, user } = state

  useEffect(() => {
    analytics.segmentTrack('Anonymous Opened Sign In/Sign Up Modal')
  }, [])

  return (
    <Modal
      outerClassName="bg-codGray"
      innerClassName="sm:bg-transparent"
      key={step}
      onAnimationComplete={() => !autoFocus && dispatch({ type: ACTIVATE_AUTOFOCUS })}
    >
      <ShowModalStep
        step={step}
        autoFocus={autoFocus}
        onClose={onClose}
        dispatch={dispatch}
        user={user}
        onComplete={onComplete}
        event={event}
        signupStep={signupStep}
        data={data}
        showCloseButton={showCloseButton}
      />
    </Modal>
  )
}

const AuthModal = ({
  show,
  event,
  onClose,
  authStep,
  signupStep,
  data,
  showCloseButton,
  bgOpacityNone,
  ...rest
}) => (
  <Overlay
    show={show}
    onClose={onClose}
    className={classnames(OVERLAY, {
      'bg-black bg-opacity-100': !authStep, // !(authStep && signupStep),
    })}
    autoFocus={false}
    preventEscBackdropClose={true}
    bgOpacityNone={true}
  >
    <InternalModal
      {...rest}
      onClose={onClose}
      event={event}
      authStep={authStep}
      signupStep={signupStep}
      data={data}
      showCloseButton={showCloseButton}
    />
  </Overlay>
)

AuthModal.defaultProps = {
  show: false,
  classNames: {},
}

export default AuthModal
